import { useState } from 'react';
import { Table, Tag, Select, Space, Button, Tooltip, App, theme, type TableColumnsType } from 'antd';
import { EditOutlined, DeleteOutlined, ProfileOutlined, DatabaseOutlined, CheckCircleOutlined, EditFilled, InboxOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { useTopics, useUpdateTopic, TOPICS_KEY } from '@/hooks/useTopics';
import { useInvestors } from '@/hooks/useInvestors';
import { useConfirmDeleteTopic } from './DeleteTopicConfirm';
import { ImportEntryDropdown } from './Import/ImportEntryDropdown';
import { readStructured, type Topic } from '@/types/topic';
import { getTopicsStats } from '@/api/topicsStats';
import { useTopicTypes } from '@/api/topicTypes';
import { PageHero, HeroChip } from '@/components/PageHero';
import { KpiTile } from '@/components/KpiTile';
import { SectionCard } from '@/components/SectionCard';

const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
];

export function TopicsListPage() {
  const { message } = App.useApp();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { token } = theme.useToken();
  const [typeId, setTypeId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [investor, setInvestor] = useState<string>('');
  const [selected, setSelected] = useState<string[]>([]);
  const updateMut = useUpdateTopic();

  const topicsQ = useTopics({
    type_id: typeId ? Number(typeId) : undefined,
    status: (status || undefined) as 'draft' | 'active' | 'archived' | undefined,
  });
  const investorsQ = useInvestors();
  const typesQ = useTopicTypes();
  const typeName = (id: number) => typesQ.data?.find((t) => t.id === id)?.name ?? `type ${id}`;
  const typeFilters = [
    { value: '', label: 'All types' },
    ...(typesQ.data ?? []).map((t) => ({ value: String(t.id), label: t.name })),
  ];
  const statsQ = useQuery({ queryKey: ['topics-stats'], queryFn: getTopicsStats });
  const confirmDel = useConfirmDeleteTopic();
  const invalidateStats = () => qc.invalidateQueries({ queryKey: ['topics-stats'] });

  const filtered = (topicsQ.data ?? []).filter((t) => {
    if (!investor) return true;
    return readStructured(t.details).investor === investor;
  });

  const stats = statsQ.data;
  const statusCount = (s: string) => stats?.by_status.find((x) => x.status === s)?.count ?? 0;

  async function bulkSetStatus(next: 'active' | 'draft') {
    try {
      for (const id of selected) {
        await updateMut.mutateAsync({ id, input: { status: next } });
      }
      message.success(`${selected.length} topic(s) marked ${next}`);
      setSelected([]);
      invalidateStats();
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  const columns: TableColumnsType<Topic> = [
    { title: 'Title (en)', key: 'title', render: (_, t) => t.details.title?.en ?? '(untitled)', ellipsis: true },
    { title: 'Investor', width: 140, render: (_, t) => readStructured(t.details).investor ?? '—' },
    { title: 'Type', dataIndex: 'type_id', width: 110, render: (v: number) => typeName(v) },
    {
      title: 'Status', dataIndex: 'status', width: 100,
      render: (v: string) => <Tag color={v === 'active' ? 'success' : v === 'draft' ? 'default' : 'warning'}>{v}</Tag>,
    },
    { title: 'Updated', dataIndex: 'edited_at', width: 140, render: (v: string) => {
      const d = v ? new Date(v) : null;
      return d && !Number.isNaN(d.getTime()) ? formatDistanceToNow(d, { addSuffix: true }) : '—';
    } },
    {
      title: 'Actions', width: 120,
      render: (_, t) => (
        <Space>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => nav(`/topics/${t.id}`)} /></Tooltip>
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />}
              onClick={async () => { if (await confirmDel(t)) { message.success('Removed'); invalidateStats(); } }} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const maxType = Math.max(1, ...(stats?.by_type ?? []).map((b) => b.count));
  const maxInv = Math.max(1, ...(stats?.by_investor ?? []).map((b) => b.count));
  const Bar = ({ label, count, max, color }: { label: string; count: number; max: number; color: string }) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color: token.colorTextSecondary }}>{count}</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: token.colorFillSecondary }}>
        <div style={{ height: 8, borderRadius: 4, width: `${Math.round((count / max) * 100)}%`, background: color }} />
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1100 }}>
      <PageHero
        icon={<ProfileOutlined />}
        title="Topics"
        subtitle="Program content across investors"
        chips={[
          <HeroChip key="total">Total {stats?.total ?? 0}</HeroChip>,
          <HeroChip key="recent">Recent 30d {stats?.recent_30d ?? 0}</HeroChip>,
        ]}
        actions={
          <ImportEntryDropdown
            onCreateBlank={() => nav('/topics/new')}
            onTopicCreated={() => { qc.invalidateQueries({ queryKey: TOPICS_KEY }); invalidateStats(); }}
            investorOptions={(investorsQ.data ?? []).map((i) => i.name)}
          />
        }
      />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <KpiTile tone="sky" icon={<DatabaseOutlined />} label="Total" value={stats?.total ?? 0} />
        <KpiTile tone="green" icon={<CheckCircleOutlined />} label="Active" value={statusCount('active')} />
        <KpiTile tone="slate" icon={<EditFilled />} label="Draft" value={statusCount('draft')} />
        <KpiTile tone="amber" icon={<InboxOutlined />} label="Archived" value={statusCount('archived')} />
      </div>

      <SectionCard tone="indigo" icon={<DatabaseOutlined />} title="Breakdown" desc="By type and top investors">
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px', minWidth: 260 }}>
            <div style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 8 }}>By type</div>
            {(stats?.by_type ?? []).map((b) => <Bar key={b.type_id} label={b.label} count={b.count} max={maxType} color="#6366f1" />)}
          </div>
          <div style={{ flex: '1 1 320px', minWidth: 260 }}>
            <div style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 8 }}>Top investors</div>
            {(stats?.by_investor ?? []).map((b) => <Bar key={b.investor} label={b.investor} count={b.count} max={maxInv} color="#0ea5e9" />)}
          </div>
        </div>
      </SectionCard>

      <SectionCard tone="indigo" icon={<ProfileOutlined />} title="All topics" desc="Filter, edit, bulk-update">
        <Space style={{ marginBottom: 12 }} wrap>
          <Select size="small" value={typeId} onChange={setTypeId} options={typeFilters} style={{ width: 160 }} />
          <Select size="small" value={status} onChange={setStatus} options={STATUS_FILTERS} style={{ width: 160 }} />
          <Select
            size="small" value={investor} onChange={setInvestor} placeholder="Investor" style={{ width: 180 }}
            options={[{ value: '', label: 'All investors' }, ...(investorsQ.data ?? []).map((i) => ({ value: i.name, label: i.name }))]}
          />
          {selected.length > 0 && (
            <>
              <Button onClick={() => bulkSetStatus('active')}>Mark active ({selected.length})</Button>
              <Button onClick={() => bulkSetStatus('draft')}>Mark draft ({selected.length})</Button>
            </>
          )}
        </Space>
        <Table
          rowKey="id"
          loading={topicsQ.isLoading}
          columns={columns}
          dataSource={filtered}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (total) => `${total} topics`,
          }}
          rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) }}
        />
      </SectionCard>
    </div>
  );
}
